import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, RefreshCw } from 'lucide-react';
import { InfoTip } from '../components/shared/FormKit';
import { useAuth } from '../contexts/AuthContext';
import { formatDateTime } from '../utils/dateFormat';
import { fetchJSON } from '../utils/api';
import { sectionTitle, sectionTitleBar } from '../components/workflow/constants';

// 'YYYY-MM-DDTHH:MM:SS' → 'YYYY-MM-DD HH:MM:SS' for display.
const fmtDateTime = (v) => (v ? formatDateTime(v) : '—');

const QMAdaptiveCookie = () => {
  const { hasPermission } = useAuth();
  const canEdit = hasPermission('qm:timesheet_ops:edit');

  const [latest, setLatest] = useState(null);
  const [loading, setLoading] = useState(true);
  const [fetching, setFetching] = useState(false);
  const [error, setError] = useState(null);
  const [message, setMessage] = useState(null);

  const loadLatest = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetchJSON('/api/qm/adaptive-cookie');
      setLatest(res.data || null);
      setError(null);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadLatest(); }, [loadLatest]);

  const handleFetch = async () => {
    setFetching(true);
    setError(null);
    setMessage(null);
    try {
      const res = await fetchJSON('/api/qm/adaptive-cookie/fetch', { method: 'POST' });
      setLatest(res.data || null);
      setMessage('Adaptive cookie fetched and saved.');
    } catch (err) {
      // The failed attempt is still recorded server-side — reflect its time.
      if (err.body?.data) setLatest(err.body.data);
      setError(err.message || 'Failed to fetch Adaptive cookie.');
    } finally {
      setFetching(false);
    }
  };

  if (!canEdit) {
    return (
      <div className="p-8 max-w-xl mx-auto">
        <div className="bg-red-50 border border-red-200 rounded-lg p-6 text-center">
          <h2 className="text-lg font-semibold text-red-800">Editor access required</h2>
          <p className="text-sm text-red-600 mt-1">You need staff records editing rights to manage the Adaptive cookie.</p>
        </div>
      </div>
    );
  }

  const isSuccess = latest && latest.success;

  return (
    <div className="p-4 sm:p-6 max-w-3xl mx-auto">
      <Link to="/manage" className="inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-700"><ArrowLeft className="w-4 h-4" />Management</Link>
      <div className="mt-2 mb-6">
        <h1 className="text-xl sm:text-2xl font-bold text-slate-900 inline-flex items-center gap-2">
          Adaptive Cookie
          <InfoTip text="The cookie is kept on the server; only the status and fetch time are shown here." />
        </h1>
        <p className="text-sm text-slate-500 mt-1">
          Fetch and store the Adaptive Pay session cookie used to pull Chargeable Hours.
        </p>
      </div>

      <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-5">
        <div>
          <div className="min-w-0">
            <h3 className={sectionTitle}>
              <span className={sectionTitleBar} aria-hidden="true" />
              Latest Fetch
            </h3>

            {loading ? (
              <p className="text-sm text-slate-500 mt-2">Loading…</p>
            ) : !latest ? (
              <p className="text-sm text-slate-500 mt-2">No cookie has been fetched yet.</p>
            ) : (
              <dl className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-2 text-sm">
                <div className="flex items-center gap-2">
                  <dt className="text-slate-500 w-24 shrink-0">Status</dt>
                  <dd>
                    {isSuccess ? (
                      <span className="text-green-600 font-medium">Success</span>
                    ) : (
                      <span className="text-red-600 font-medium">Failed</span>
                    )}
                  </dd>
                </div>
                <div className="flex items-center gap-2">
                  <dt className="text-slate-500 w-24 shrink-0">Fetched At</dt>
                  <dd className="text-slate-900">{fmtDateTime(latest.fetched_at)}</dd>
                </div>
                <div className="flex items-center gap-2">
                  <dt className="text-slate-500 w-24 shrink-0">Fetched By</dt>
                  <dd className="text-slate-900">{latest.fetched_by || '—'}</dd>
                </div>
                {!isSuccess && latest.error && (
                  <div className="flex items-start gap-2 sm:col-span-2">
                    <dt className="text-slate-500 w-24 shrink-0">Error</dt>
                    <dd className="text-red-600 break-words">{latest.error}</dd>
                  </div>
                )}
              </dl>
            )}
          </div>
        </div>

        <div className="mt-5 pt-4 border-t border-slate-100 flex items-center gap-3">
          <button
            onClick={handleFetch}
            disabled={fetching}
            className="px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 disabled:opacity-50 inline-flex items-center gap-2"
          >
            <RefreshCw className={`w-4 h-4 ${fetching ? 'animate-spin' : ''}`} />
            {fetching ? 'Fetching…' : 'Fetch Cookie Now'}
          </button>
          {message && (
            <span className="text-sm text-green-600">{message}</span>
          )}
          {error && (
            <span className="text-sm text-red-600">{error}</span>
          )}
        </div>
      </div>
    </div>
  );
};

export default QMAdaptiveCookie;
